import { Component } from "react"
import {
    ShieldCheck, Clock, Users, BadgeCheck,
    Tag, MapPin, TrendingUp, Scale, Crown, LucideIcon,
} from "lucide-react"
import UserPermissionHelper from "src/utils/UserPermissionHelper"
import UserFeatureHelper from "src/utils/UserFeatureHelper"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import ApiHandler from "src/api/ApiHandler"
import ErrorHandler from "src/components/errors/ErrorHandler"
import UserVisibleErrorStoringDecorator from "src/components/decorators/UserErrorStoringDecorator"
import StateConstants from "src/states/StateContstants"
import Loader from "src/components/general/Loading"
import ValuationLoader from "src/components/general/ValuationLoader"
import { Helmet } from 'react-helmet-async';
import SearchForm from "src/components/search/SearchForm"
import navigationService from "src/navigation/NavigationService"
import "./Search.css"

const LANDING_ASSET_PATH = "/landing/"

const TRUST_ITEMS: { title: string; copy: string; Icon: LucideIcon }[] = [
    { title: "Trusted data sources", copy: "CSO, RTB, Land Registry & more", Icon: ShieldCheck },
    { title: "Updated daily", copy: "Fresh valuations and insights", Icon: Clock },
    { title: "Buyers & sellers", copy: "Clarity before you commit", Icon: Users },
    { title: "100% independent", copy: "Unbiased. Always.", Icon: BadgeCheck },
]

const FEATURE_CARDS: { title: string; copy: string; Icon: LucideIcon; image: string; alt: string }[] = [
    { title: "Instant Valuation", copy: "Get an accurate estimate in seconds.", Icon: Tag, image: `${LANDING_ASSET_PATH}feature-instant-valuation-photo.png`, alt: "Instant house valuation showing estimated property value and price range" },
    { title: "Area Insights", copy: "Schools, transport, amenities & more.", Icon: MapPin, image: `${LANDING_ASSET_PATH}feature-area-insights-photo.png`, alt: "Area insights showing schools, transport and local amenities for an Irish neighbourhood" },
    { title: "Market Trends", copy: "See price history and local demand.", Icon: TrendingUp, image: `${LANDING_ASSET_PATH}feature-market-trends-photo.png`, alt: "Property market trends showing house price history and demand in Ireland" },
    { title: "Compare Areas", copy: "Find the best fit for your budget.", Icon: Scale, image: `${LANDING_ASSET_PATH}feature-compare-areas-photo.png`, alt: "Compare house prices across different areas and towns in Ireland" },
]

class LandingPage extends Component<any> {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    errorHandler: ErrorHandler
    state: any
    loader: Loader

    constructor(props: any) {
        super(props)
        this.stateHandler = props.stateHandler
        this.apiHandler = props.apiHandler
        this.errorHandler = props.errorHandler
        this.loader = props.loader
        this.state = {
            isLoading: false,
            isLoggedIn: !!this.stateHandler.get(StateConstants.IS_LOGGED_IN),
            isPremium: UserFeatureHelper.hasFeature(this.stateHandler, UserFeatureHelper.DETAILED_VALUATIONS),
            canViewAnalysis: UserPermissionHelper.canViewAnalysisDashboard(this.stateHandler),
            isMenuOpen: false,
        }
        this.stateHandler.addListener(StateConstants.IS_LOADING, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                isLoading: state
            }))
        })
        this.stateHandler.addListener(StateConstants.IS_LOGGED_IN, (state: any) => {
            this.setState({ isLoggedIn: !!state })
        })
        this.stateHandler.addListener(StateConstants.USER_FEATURES, () => {
            this.setState({ isPremium: UserFeatureHelper.hasFeature(this.stateHandler, UserFeatureHelper.DETAILED_VALUATIONS) })
        })
        this.stateHandler.addListener(StateConstants.USER_PERMISSIONS, () => {
            this.setState({ canViewAnalysis: UserPermissionHelper.canViewAnalysisDashboard(this.stateHandler) })
        })
        this.stateHandler.addListener(StateConstants.ERROR, () => {
            this.setState({})
        })
    }

    @UserVisibleErrorStoringDecorator()
    async search(event: any) {
        event.preventDefault()
        let address = this.stateHandler.get(StateConstants.ADDRESS_STATE)
        if (address === undefined || address === "") {
            return
        }

        let input = { "address": address }

        let foundAddress = await this.apiHandler.findAddress(input)
        let id = foundAddress["gi"]
        this.redirectToValuationPage(id, foundAddress["uid"])
    }

    redirectToValuationPage(id: string, uniqueId?: string) {
        navigationService.navigateToValuation(id, this.apiHandler, undefined, undefined, uniqueId)
    }

    closeMenu = () => {
        this.setState({ isMenuOpen: false })
    }

    toggleMenu = () => {
        this.setState((prev: any) => ({ isMenuOpen: !prev.isMenuOpen }))
    }

    openPlans = () => {
        this.closeMenu()
        this.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, true)
    }

    openLogin = () => {
        this.closeMenu()
        this.stateHandler.set(StateConstants.LOGIN_MODAL_MODE, "login")
        this.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
    }

    openSignUp = () => {
        this.closeMenu()
        this.stateHandler.set(StateConstants.LOGIN_MODAL_MODE, "signup")
        this.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
    }

    logout = async () => {
        this.closeMenu()
        try { await this.props.loginHelper?.logout() } catch { /* ignore */ }
    }

    navigateTo = (path: string) => {
        this.closeMenu()
        navigationService.navigateTo(path)
    }

    renderLandingBrand(asLink: boolean = false) {
        const content = (
            <>
                <span className="landing-brand-mark" aria-hidden>
                    <img src="/homeindex_logo.svg" alt="HomeIndex" width="38" height="38" />
                </span>
                <span className="landing-brand-text">HomeIndex.ie</span>
            </>
        )

        if (asLink) {
            return <a href="/" className="landing-brand" aria-label="HomeIndex - Home">{content}</a>
        }

        return <span className="landing-brand">{content}</span>
    }

    renderFeaturedPropertyDashboard() {
        return (
            <section className="landing-insight-dashboard" aria-label="Example property insight">
                <div className="landing-dashboard-header">
                    <span className="landing-dashboard-label">Featured property</span>
                </div>

                <div className="landing-featured-property">
                    <img
                        className="landing-featured-property-image"
                        src={`${LANDING_ASSET_PATH}featured-property-rathgar.png`}
                        alt="Period home on Wellington Road in Rathgar"
                    />
                    <div className="landing-featured-property-details">
                        <h2>101 Wellington Road</h2>
                        <span className="landing-featured-area">Rathgar, Dublin 6</span>
                        <span className="landing-property-facts">4 bed · 2 bath · End of terrace</span>
                        <span className="landing-value-label">HomeIndex Value</span>
                        <strong className="landing-featured-value">€785,000</strong>
                        <span className="landing-value-range">Estimate range: €745,000 – €825,000</span>
                        <span className="landing-value-change">↑ 3.2% vs. last 3 months</span>
                    </div>
                </div>

                <div className="landing-dashboard-panels">
                    <button
                        type="button"
                        className="landing-panel-card"
                        onClick={() => this.navigateTo("/areas")}
                        aria-label="Explore the Rathgar, Dublin 6 area"
                    >
                        <img
                            className="landing-panel-fill"
                            src={`${LANDING_ASSET_PATH}hero-area-insight.png`}
                            alt="Area insight for Rathgar, Dublin 6 — strong demand, great schools, excellent amenities"
                        />
                    </button>

                    <button
                        type="button"
                        className="landing-panel-card"
                        onClick={() => this.navigateTo("/areas")}
                        aria-label="View Dublin 6 market insights"
                    >
                        <img
                            className="landing-panel-fill"
                            src={`${LANDING_ASSET_PATH}hero-market-trend.png`}
                            alt="Dublin 6 market trend — +3.2% annual price growth"
                        />
                    </button>
                </div>
            </section>
        )
    }

    render() {
        const { isLoading } = this.state
        const blogIndexPath = "/blog/"

        if (isLoading) {
            return <ValuationLoader />
        }

        return (
            <div className="landing-new-view">
                <Helmet>
                    <title>{`HomeIndex - Free House Valuations Ireland | How Much Is My House Worth`}</title>
                    <meta name="description" content="Get a free, instant house valuation for any property in Ireland. See the estimated value, comparable sales nearby, price trends and area insights. Data-driven, independent valuations updated daily." />
                    <link rel="canonical" href="https://homeindex.ie/" />
                    <script type="application/ld+json">{JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        "name": "HomeIndex",
                        "url": "https://homeindex.ie",
                        "description": "Free, instant property valuations for any home in Ireland",
                        "potentialAction": {
                            "@type": "SearchAction",
                            "target": "https://homeindex.ie/search?q={search_term_string}",
                            "query-input": "required name=search_term_string"
                        }
                    })}</script>
                    <script type="application/ld+json">{JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "HomeIndex",
                        "url": "https://homeindex.ie",
                        "description": "Independent property valuation service for Ireland",
                        "address": {
                            "@type": "PostalAddress",
                            "addressCountry": "IE"
                        }
                    })}</script>
                    <script type="application/ld+json">{JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "How much is my house worth in Ireland?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Enter any Irish address on HomeIndex to get a free, instant property valuation. Our estimates use data from the CSO, Property Price Register, RTB and Land Registry, combined with machine learning models trained on recent sales. You'll see an estimated value with a confidence range, comparable sales nearby, and full price history."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "How does HomeIndex calculate property valuations?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "HomeIndex uses machine learning models trained on official Irish data sources including the Property Price Register, CSO census data, and RTB rental data. The model considers comparable sales nearby, property features (bedrooms, bathrooms, floor area), location, market trends and local demand to estimate each property's current market value."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Is HomeIndex free to use?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes, you can get a free property valuation by signing up for a free account. Free accounts include one full valuation with the exact estimate. For unlimited valuations, comparable sales, and complete price history, Lifetime Access is available for a one-time payment of €19."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "What data sources does HomeIndex use?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "HomeIndex uses official Irish data sources: the Central Statistics Office (CSO), the Property Price Register (Land Registry), the Residential Tenancies Board (RTB), and OpenStreetMap for geographic and amenity data. All data is updated daily."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Can I check house prices in a specific area?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes. Visit the Areas section on HomeIndex to search any Irish town, suburb or postcode. You'll see median property prices, recent sales, price trends, local demand indicators, and information about schools, transport and amenities in the area."
                                }
                            }
                        ]
                    })}</script>
                </Helmet>
                {this.errorHandler.render(this.stateHandler.get("error"), {
                    onDismiss: () => this.stateHandler.set(StateConstants.ERROR, null)
                })}
                <span className="landing-sr-compat">Value your home</span>

                <section className="valuations-home-page landing-hero">
                    <header className={"landing-nav" + (this.state.isMenuOpen ? " landing-nav-open" : "")}>
                        {this.renderLandingBrand(true)}
                        <button
                            type="button"
                            className="landing-nav-toggle"
                            aria-label="Toggle menu"
                            aria-expanded={this.state.isMenuOpen}
                            onClick={this.toggleMenu}
                        >
                            <span aria-hidden>{this.state.isMenuOpen ? "✕" : "☰"}</span>
                        </button>
                        <nav className="landing-nav-links" aria-label="Primary">
                            <button type="button" onClick={() => this.navigateTo("/")}>Valuations</button>
                            <button type="button" onClick={() => this.navigateTo("/reports")}>Reports</button>
                            <a href={blogIndexPath} onClick={this.closeMenu}>Blog</a>
                            {this.state.canViewAnalysis && (
                                <button type="button" className="landing-nav-analysis" onClick={() => this.navigateTo("/analysis")}>
                                    Analysis
                                </button>
                            )}
                        </nav>
                        <div className="landing-auth-actions">
                            {this.state.isLoggedIn ? (
                                <button className="landing-login-button" type="button" onClick={this.logout}>Log out</button>
                            ) : (
                                <>
                                    <button className="landing-signup-button" type="button" onClick={this.openSignUp}>Sign up</button>
                                    <button className="landing-login-button" type="button" onClick={this.openLogin}>Log in</button>
                                </>
                            )}
                            {this.state.isLoggedIn && this.state.isPremium && (
                                <span className="header-premium-badge" title="Lifetime Access" aria-label="Lifetime Access">
                                    <Crown size={14} strokeWidth={2} />
                                    <span>Lifetime</span>
                                </span>
                            )}
                        </div>
                    </header>

                    <div className="landing-hero-grid">
                        <div className="landing-hero-copy">
                            <h1>How much is your<br />home worth?</h1>
                            <p>Free, instant house valuations for any property in Ireland. Comparable sales, price trends and area insights.</p>
                            <div className="landing-search-shell">
                                <SearchForm
                                    stateHandler={this.stateHandler}
                                    apiHandler={this.apiHandler}
                                    border={false}
                                    placeholder="Enter an address or postcode"
                                    showSubmitButton
                                />
                            </div>
                            <div className="landing-pill-row" aria-label="Quick actions">
                                <a href="/how-it-works">How it works</a>
                                <a href="/plans">Buy or sell with confidence</a>
                                <a href="/areas">See area insights</a>
                            </div>
                        </div>
                        {this.renderFeaturedPropertyDashboard()}
                    </div>
                </section>

                <main className="landing-main">
                    <section className="landing-trust-strip" aria-label="HomeIndex trust signals">
                        {TRUST_ITEMS.map(({ title, copy, Icon }) => (
                            <div className="landing-trust-item" key={title}>
                                <span className="landing-trust-icon" aria-hidden>
                                    <Icon size={22} strokeWidth={2} />
                                </span>
                                <div>
                                    <strong>{title}</strong>
                                    <span>{copy}</span>
                                </div>
                            </div>
                        ))}
                    </section>

                    <section className="landing-feature-section">
                        <h2>Why people choose HomeIndex</h2>
                        <div className="landing-feature-grid">
                            {FEATURE_CARDS.map(({ title, copy, Icon, image, alt }) => (
                                <article className="landing-feature-card" key={title}>
                                    <div className="landing-feature-card-copy">
                                        <span className="landing-feature-icon" aria-hidden>
                                            <Icon size={22} strokeWidth={2} />
                                        </span>
                                        <div>
                                            <h3>{title}</h3>
                                            <p>{copy}</p>
                                        </div>
                                    </div>
                                    <img className="landing-feature-illustration" src={image} alt={alt} />
                                </article>
                            ))}
                        </div>
                    </section>

                </main>

                <footer className="landing-footer">
                    <div className="landing-footer-brand">
                        {this.renderLandingBrand()}
                        <p>Property intelligence for buyers and sellers.<br />Local knowledge. Smarter decisions.</p>
                        <div className="landing-social-row" aria-label="Social links">
                            <a href="https://www.facebook.com/" aria-label="Facebook">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9.198 21.5h4v-8.01h3.604l.396-3.98h-4V7.5a1 1 0 011-1h3v-4h-3a5 5 0 00-5 5v2.01h-2l-.396 3.98h2.396v8.01z"/></svg>
                            </a>
                            <a href="https://twitter.com/" aria-label="X">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23.643 4.937a9.57 9.57 0 01-2.828.775 4.889 4.889 0 002.165-2.724 9.664 9.664 0 01-3.127 1.195A4.916 4.916 0 0016.616 3c-2.737 0-4.952 2.235-4.952 4.99 0 .39.045.77.127 1.134C7.728 8.89 4.1 6.89 1.671 3.905a5.048 5.048 0 00-.67 2.508c0 1.732.878 3.26 2.212 4.154a4.904 4.904 0 01-2.244-.622v.063c0 2.418 1.712 4.434 3.983 4.892a4.935 4.935 0 01-2.237.085c.63 1.972 2.457 3.408 4.624 3.448A9.868 9.868 0 010 20.544a13.94 13.94 0 007.548 2.212c9.057 0 14.01-7.514 14.01-14.024 0-.213-.005-.426-.015-.637A10.025 10.025 0 0024 4.59a9.722 9.722 0 01-2.357.346z"/></svg>
                            </a>
                            <a href="https://www.linkedin.com/" aria-label="LinkedIn">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                            </a>
                            <a href="https://www.instagram.com/" aria-label="Instagram">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                            </a>
                        </div>
                    </div>
                        <div className="landing-footer-links">
                        <div>
                            <strong>Product</strong>
                            <a href="/">Valuations</a>
                            <a href="/areas">Areas</a>
                            <a href="/plans">Reports</a>
                            <a href="/plans">Pricing</a>
                        </div>
                        <div>
                            <strong>Resources</strong>
                            <a href={blogIndexPath}>Blog</a>
                            <a href={blogIndexPath}>Market Insights</a>
                            <a href={blogIndexPath}>Buying Guide</a>
                            <a href="/contact">Help Centre</a>
                            <a href={blogIndexPath}>Glossary</a>
                        </div>
                        <div>
                            <strong>Company</strong>
                            <a href="/contact">About Us</a>
                            <a href="/contact">Contact Us</a>
                        </div>
                        <div>
                            <strong>Legal</strong>
                            <a href="/terms">Terms of Use</a>
                            <a href="/terms">Privacy Policy</a>
                            <a href="/terms">Cookie Policy</a>
                            <a href="/terms">Data Protection</a>
                        </div>
                    </div>
                    <div className="landing-footer-bottom">
                        <span>© 2026 HomeIndex. All rights reserved.</span>
                        <span><span className="landing-proudly">Proudly Irish</span> <span className="irish-flag" aria-hidden /></span>
                    </div>
                </footer>
            </div>
        )
    }
}

export default LandingPage
